import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Calendar,
  Plus,
  Clock,
  CheckCircle,
  Users,
  Loader2,
  ArrowRight,
  Ticket,
  Send,
  AlertTriangle,
  Video,
  Download,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useUserStats, AdminDashboardStats } from '../hooks/useUserStats';
import { useTickets } from '../hooks/useTickets';
import LoadingSpinner from '../components/LoadingSpinner';
import Button from '../components/Button';
import TicketStatusBadge from '../components/TicketStatusBadge';
import {
  isAdmin,
  isCustomerSupport,
  isVideoRecorder,
  normalizeStatus,
} from '../utils/roles';
import { formatSlot, userDisplayName } from '../utils/ticketDisplay';

type StatCard = {
  name: string;
  value: number | string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  href: string;
};

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const role = user?.role;
  const adminView = isAdmin(role);
  const supportView = isCustomerSupport(role);
  const recorderView = isVideoRecorder(role);

  const { data: stats, isLoading: statsLoading } = useUserStats();
  const { data: ticketsData } = useTickets();

  const tickets = stats?.tickets;
  const recentTickets = stats?.recentTickets ?? [];
  const activeTickets = stats?.activeTickets ?? [];
  const adminStats = stats as AdminDashboardStats | undefined;

  const nextQueueTicket = useMemo(() => {
    const list = ticketsData?.tickets ?? [];
    const open = list.find((t) => normalizeStatus(t.status) === 'open');
    if (open) return open;
    return list.find(
      (t) =>
        (normalizeStatus(t.status) === 'accepted' || normalizeStatus(t.status) === 'recording') &&
        t.assignedTo?.id === user?.id
    );
  }, [ticketsData, user?.id]);

  if (statsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
        <span className="ml-3 text-gray-600">Loading dashboard...</span>
      </div>
    );
  }

  const t = tickets ?? {
    total: 0,
    open: 0,
    accepted: 0,
    recording: 0,
    processing: 0,
    completed: 0,
    sent: 0,
    cancelled: 0,
    failed: 0,
    pipeline: 0,
    needsAttention: 0,
    readyToSend: 0,
    myOpenQueue: 0,
    myActive: 0,
    thisWeek: 0,
    thisMonth: 0,
  };

  const ticketsBase = adminView || supportView ? '/tickets' : '/my-tickets';

  let statCards: StatCard[] = [];

  if (adminView) {
    statCards = [
      { name: 'Total tickets', value: t.total, icon: Ticket, color: 'bg-slate-600', href: ticketsBase },
      { name: 'Open', value: t.open, icon: Calendar, color: 'bg-amber-500', href: `${ticketsBase}?status=open` },
      { name: 'In pipeline', value: t.pipeline, icon: Loader2, color: 'bg-sky-500', href: ticketsBase },
      { name: 'Ready to send', value: t.readyToSend, icon: CheckCircle, color: 'bg-emerald-500', href: `${ticketsBase}?status=completed` },
      { name: 'Sent', value: t.sent, icon: Send, color: 'bg-green-600', href: `${ticketsBase}?status=sent` },
      { name: 'Portal users', value: adminStats?.totalUsers ?? 0, icon: Users, color: 'bg-indigo-500', href: '/users' },
    ];
  } else if (supportView) {
    statCards = [
      { name: 'Total tickets', value: t.total, icon: Ticket, color: 'bg-slate-600', href: ticketsBase },
      { name: 'Open', value: t.open, icon: Calendar, color: 'bg-amber-500', href: `${ticketsBase}?status=open` },
      { name: 'In pipeline', value: t.pipeline, icon: Loader2, color: 'bg-sky-500', href: ticketsBase },
      { name: 'Ready to send', value: t.readyToSend, icon: CheckCircle, color: 'bg-emerald-500', href: `${ticketsBase}?status=completed` },
      { name: 'Sent', value: t.sent, icon: Send, color: 'bg-green-600', href: `${ticketsBase}?status=sent` },
      { name: 'This month', value: t.thisMonth, icon: Clock, color: 'bg-violet-500', href: ticketsBase },
    ];
  } else if (recorderView) {
    statCards = [
      { name: 'Open queue', value: t.myOpenQueue, icon: Ticket, color: 'bg-amber-500', href: '/my-tickets' },
      { name: 'My active', value: t.myActive, icon: Loader2, color: 'bg-sky-500', href: '/my-tickets' },
      { name: 'Recording', value: t.recording, icon: Video, color: 'bg-blue-500', href: '/my-tickets' },
      { name: 'Ready to send', value: t.readyToSend, icon: CheckCircle, color: 'bg-emerald-500', href: '/my-tickets' },
      { name: 'Sent', value: t.sent, icon: Send, color: 'bg-green-600', href: '/my-tickets' },
    ];
  } else {
    statCards = [
      { name: 'Total tickets', value: t.total, icon: Ticket, color: 'bg-slate-600', href: ticketsBase },
      { name: 'Needs attention', value: t.needsAttention, icon: AlertTriangle, color: 'bg-amber-500', href: ticketsBase },
      { name: 'Sent', value: t.sent, icon: Send, color: 'bg-green-600', href: ticketsBase },
    ];
  }

  const welcomeSubtitle = adminView
    ? 'Overview of recording tickets across your branches.'
    : supportView
      ? 'Track open requests, pipeline progress, and videos ready to deliver.'
      : recorderView
        ? 'Your queue: accept tickets, record sessions, and hand off for delivery.'
        : 'Recording ticket overview.';

  return (
    <div className="space-y-6">
      <div className="etr-surface overflow-hidden">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex-1">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
                Welcome back, {user?.firstName || user?.email || 'User'}!
              </h1>
              <p className="mt-1 text-sm text-gray-500">{welcomeSubtitle}</p>
            </div>
            {recorderView && nextQueueTicket && (
              <Link
                to={
                  normalizeStatus(nextQueueTicket.status) === 'open'
                    ? `/my-tickets`
                    : `/sessions/new?ticketId=${nextQueueTicket.id}`
                }
                className="flex-shrink-0"
              >
                <Button variant="primary" size="lg" leftIcon={<Video className="h-5 w-5" />} className="w-full sm:w-auto">
                  {normalizeStatus(nextQueueTicket.status) === 'open' ? 'Go to queue' : 'Continue ticket'}
                </Button>
              </Link>
            )}
            {supportView && (
              <Link to="/tickets" className="flex-shrink-0">
                <Button variant="primary" size="lg" leftIcon={<Plus className="h-5 w-5" />} className="w-full sm:w-auto">
                  View tickets
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-5">
        {statCards.map((card) => {
          const Icon = card.icon;
          return (
            <Link
              key={card.name}
              to={card.href}
              className="relative etr-surface pt-4 px-3 pb-8 sm:pt-5 sm:px-4 sm:pb-10 overflow-hidden hover:shadow-md transition-shadow"
            >
              <dt>
                <div className={`absolute ${card.color} rounded-md p-2 sm:p-3`}>
                  <Icon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </div>
                <p className="ml-11 sm:ml-14 text-xs font-medium text-gray-500 leading-tight">{card.name}</p>
              </dt>
              <dd className="ml-11 sm:ml-14 pb-4 flex items-baseline mt-1">
                <p className="text-xl font-semibold text-gray-900">{card.value}</p>
              </dd>
            </Link>
          );
        })}
      </div>

      {(t.failed > 0 || t.cancelled > 0) && (
        <div className="flex flex-wrap gap-3 text-sm">
          {t.failed > 0 && (
            <Link to={ticketsBase} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-red-50 text-red-800 border border-red-200">
              <AlertTriangle className="h-4 w-4" />
              {t.failed} failed
            </Link>
          )}
          {t.cancelled > 0 && (
            <span className="inline-flex items-center px-3 py-1.5 rounded-md bg-gray-100 text-gray-700 border border-gray-200">
              {t.cancelled} cancelled
            </span>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="etr-surface">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900 flex items-center">
                <Loader2 className="h-5 w-5 text-sky-500 mr-2" />
                Active work
              </h3>
              <Link to={recorderView ? '/my-tickets' : ticketsBase} className="text-sm text-blue-600 hover:text-blue-800 flex items-center">
                View all
                <ArrowRight className="h-4 w-4 ml-1" />
              </Link>
            </div>
            {activeTickets.length > 0 ? (
              <div className="space-y-3">
                {activeTickets.map((ticket) => (
                  <Link
                    key={ticket.id}
                    to={`/tickets/${ticket.id}`}
                    className="flex items-center justify-between p-3 bg-sky-50 rounded-md hover:bg-sky-100 transition-colors border border-sky-200"
                  >
                    <div className="flex-1 min-w-0 pr-3">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {ticket.ticketNumber ? `${ticket.ticketNumber} · ` : ''}
                        {ticket.title}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {ticket.slotStartAt ? formatSlot(ticket.slotStartAt) : 'No slot'}
                        {ticket.assignedTo && (
                          <span className="ml-2">· {userDisplayName(ticket.assignedTo)}</span>
                        )}
                      </p>
                    </div>
                    <TicketStatusBadge status={ticket.status} />
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-sm text-gray-500">No tickets in accepted, recording, or processing.</div>
            )}
          </div>
        </div>

        <div className="etr-surface">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Recent tickets</h3>
              <Link to={ticketsBase} className="text-sm text-blue-600 hover:text-blue-800 flex items-center">
                View all
                <ArrowRight className="h-4 w-4 ml-1" />
              </Link>
            </div>
            {recentTickets.length > 0 ? (
              <div className="space-y-3">
                {recentTickets.slice(0, 6).map((ticket) => (
                  <Link
                    key={ticket.id}
                    to={`/tickets/${ticket.id}`}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-md hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center flex-1 min-w-0 pr-3">
                      <Ticket className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{ticket.title}</p>
                        <p className="text-xs text-gray-500 truncate">
                          {ticket.ticketNumber ?? ticket.id.slice(0, 8)}
                          {adminView && ticket.requestedBy && (
                            <span className="ml-1">· {userDisplayName(ticket.requestedBy)}</span>
                          )}
                        </p>
                      </div>
                    </div>
                    <TicketStatusBadge status={ticket.status} />
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <Ticket className="mx-auto h-12 w-12 text-gray-300" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No tickets yet</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {supportView ? 'Create a ticket from the Tickets page when a session is booked.' : 'Tickets will appear here as they are created.'}
                </p>
              </div>
            )}
          </div>
        </div>

        {adminView && adminStats?.byRecorder && adminStats.byRecorder.length > 0 ? (
          <div className="etr-surface">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Recorder workload</h3>
              <div className="space-y-3">
                {adminStats.byRecorder.map((row) => (
                  <div key={row.userId} className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                    <div className="flex items-center min-w-0">
                      <Users className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {row.userName || row.userEmail || 'Recorder'}
                        </p>
                        <p className="text-xs text-gray-500">{row.total} assigned · {row.active} active</p>
                      </div>
                    </div>
                    <span className="text-xs text-green-700 font-medium">{row.completed} sent</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="etr-surface">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Quick actions</h3>
              <div className="space-y-3">
                {recorderView && (
                  <>
                    <Link to="/my-tickets" className="block">
                      <Button variant="primary" size="sm" leftIcon={<Video className="h-4 w-4" />} className="w-full justify-start">
                        My ticket queue
                      </Button>
                    </Link>
                    <Link to="/install-recorder" className="block">
                      <Button variant="outline" size="sm" leftIcon={<Download className="h-4 w-4" />} className="w-full justify-start">
                        Install desktop recorder
                      </Button>
                    </Link>
                  </>
                )}
                {(adminView || supportView) && (
                  <Link to="/tickets" className="block">
                    <Button variant="primary" size="sm" leftIcon={<Ticket className="h-4 w-4" />} className="w-full justify-start">
                      All tickets
                    </Button>
                  </Link>
                )}
                {adminView && (
                  <Link to="/users" className="block">
                    <Button variant="outline" size="sm" leftIcon={<Users className="h-4 w-4" />} className="w-full justify-start">
                      Manage users
                    </Button>
                  </Link>
                )}
                <Link to="/profile" className="block">
                  <Button variant="outline" size="sm" leftIcon={<Clock className="h-4 w-4" />} className="w-full justify-start">
                    Profile &amp; account
                  </Button>
                </Link>
              </div>
              {!adminView && !supportView && !recorderView && (
                <p className="mt-4 text-xs text-gray-500">
                  This week: {t.thisWeek} · This month: {t.thisMonth}
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
